import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ICON } from '@core/util/icon';
import { noOp } from '@core/util/noOp';
import {
	Company,
	Execution,
	Person,
	WritSearchItem,
	WritSearchType,
} from '@models/teranet';
import { Observable } from 'rxjs';
import {
	distinctUntilChanged,
	map,
	shareReplay,
	startWith,
} from 'rxjs/operators';
import { TestHooks } from './search-item.hooks';

interface RawFormData {
	id: string;
	type: WritSearchType;
	firstName: string;
	lastName: string;
	name: string;
	number: string;
}

interface FormDataPerson {
	type: WritSearchType.PERSON;
	firstName: string;
	lastName: string;
}

interface FormDataCompany {
	type: WritSearchType.COMPANY;
	name: string;
}

interface FormDataExecution {
	type: WritSearchType.EXECUTION;
	number: string;
}

export type SearchItemComponentClose = WritSearchItem;

export interface SearchItemComponentData {
	existing?: boolean;
	searchItem: WritSearchItem;
}

@Component({
	selector: 'app-search-item',
	templateUrl: './search-item.component.html',
	standalone: false,
})
export class SearchItemComponent implements OnInit {
	testHooks = TestHooks;
	static writSearchTypeOptions = [
		{ value: WritSearchType.PERSON, label: 'Person' },
		{ value: WritSearchType.COMPANY, label: 'Company' },
		{ value: WritSearchType.EXECUTION, label: 'Execution number' },
	];

	static hasLoggedMissingType = false;

	static writSearchTypeControls;

	disableSubmit$!: Observable<boolean>;

	formGroup!: FormGroup;

	isEditExisting: boolean;

	ICON = ICON;

	noOp = noOp;

	// the values passed to the radio group
	typeOptions = SearchItemComponent.writSearchTypeOptions;

	value$!: Observable<SearchItemComponentClose>;

	WRIT_SEARCH_TYPE = WritSearchType;

	constructor(
		@Inject(MAT_DIALOG_DATA) private data: SearchItemComponentData,
		private fb: FormBuilder,
	) {}

	ngOnInit(): void {
		const { data, fb } = this;
		const { existing, searchItem } = data || {};
		const { item } = searchItem || {};
		const id = item?.id || null;

		// create our form
		this.formGroup = fb.group({
			id,
			type: searchItem?.type || WritSearchType.PERSON,
			name: (item as Company)?.name || '',
			firstName: (item as Person)?.firstName || '',
			lastName: (item as Person)?.lastName || '',
			number: (item as Execution)?.number || '',
		});

		// the id never changes, so it can be a simple binding
		this.isEditExisting = existing;

		// coerce our value getter to a more friendly
		// and typed value def
		this.value$ = this.formGroup.valueChanges.pipe(
			startWith(this.formGroup.value),
			map((value) => (value || {}) as RawFormData),
			map(({ type, firstName, lastName, number, name }: RawFormData) => {
				let value!: SearchItemComponentClose;
				switch (type) {
					case WritSearchType.COMPANY:
						value = { type, item: { name: name?.trim() } };
						break;

					case WritSearchType.PERSON:
						value = {
							type,
							item: {
								firstName: firstName?.trim(),
								lastName: lastName?.trim(),
							},
						};
						break;

					case WritSearchType.EXECUTION:
						value = { type, item: { number: number?.trim() } };
						break;
				}

				return value;
			}),
			shareReplay(),
		);

		// simple check since we now have explicit types,
		// we just need to make sure everything has a value that isn't empty
		this.disableSubmit$ = this.value$.pipe(
			map((value) => !this.validateEntry(value)), // makes sure every key has a value, easy-peasy
			distinctUntilChanged(),
		);
	}

	validateEntry = (value: SearchItemComponentClose): boolean => {
		switch (value.type) {
			case WritSearchType.COMPANY:
				return (value.item as Company)?.name?.trim()?.length > 0;

			case WritSearchType.PERSON:
				return (
					(value.item as Person)?.firstName?.trim()?.length > 0 &&
					(value.item as Person)?.lastName?.trim()?.length > 0
				);
				break;

			case WritSearchType.EXECUTION:
				return (value.item as Execution).number?.trim()?.length > 0;
				break;
		}
	};
}
